import { useMemo } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button, IconButton, Wirkungsbalken, formatEuro, formatMonat } from '@elbklar/ui';

import type { DraftImpact } from '@/types';

interface Props {
  wirkungen: DraftImpact[];
  ausgewaehlt: ReadonlySet<string>;
  onUmschalten: (id: string) => void;
  onFest: (id: string) => void;
  onBearbeiten: (id: string) => void;
  onLoeschen: (id: string) => void;
}

/**
 * Alle Entwürfe nebeneinander, sortiert nach Wirkung auf den Tiefpunkt.
 *
 * Das An- und Abwählen einzelner Posten beantwortet nur „was kostet dieser?“.
 * Die eigentliche Entscheidungsfrage ist aber „welche Option ist die
 * teuerste?“, und die sieht man erst, wenn alle nebeneinander stehen.
 * Abflüsse nach links, Zuflüsse nach rechts: So ist auf einen Blick sichtbar,
 * ob der geplante Umsatz die geplanten Ausgaben überhaupt trägt.
 *
 * Die Balken sind NICHT addierbar. Jede Wirkung ist einzeln gegen dieselbe
 * Basis gerechnet; zwei Posten im selben Monat wirken zusammen anders. Für die
 * Kombination gibt es die Auswahl und den Kontoverlauf darüber.
 */
export function EntwurfsWirkungen({ wirkungen, ausgewaehlt, onUmschalten, onFest, onBearbeiten, onLoeschen }: Props) {
  const sortiert = useMemo(
    () => [...wirkungen].sort((a, b) => Number(a.min_balance_delta) - Number(b.min_balance_delta)),
    [wirkungen],
  );

  return (
    <Wirkungsbalken
      links="kostet"
      rechts="bringt"
      ausgewaehlt={ausgewaehlt}
      onUmschalten={onUmschalten}
      leer="Keine Entwürfe. Eine geplante Ausgabe anlegen, um sie durchzurechnen."
      fuss="Jede Wirkung ist einzeln gegen dieselbe Ausgangslage gerechnet: vergleichbar, aber nicht addierbar. Für Kombinationen die Auswahl oben nutzen."
      zeilen={sortiert.map((w) => {
        const tiefpunkt = Number(w.min_balance_delta);
        return {
          id: w.planned_item_id,
          name: w.name,
          wert: tiefpunkt,
          wirkung: monatsText(w.runway_delta_months),
          // Ein Posten kann groß sein und trotzdem nichts bewegen, etwa Umsatz,
          // der erst NACH dem Kipppunkt eingeht. Das darf nicht grün aussehen.
          bewegt: w.runway_delta_months !== 0,
          // Ohne Vorzeichen: Die Seite des Balkens sagt schon, ob er kostet oder bringt.
          betrag: formatEuro(Math.abs(Number(w.total_amount))),
          detail: `Tiefpunkt ${tiefpunkt > 0 ? '+' : '−'}${formatEuro(Math.abs(tiefpunkt))}`,
          folge: `reicht dann bis ${reichtBisText(w)}`,
          aktionen: (
            <>
              <Button size="sm" variant="secondary" onClick={() => onFest(w.planned_item_id)}>
                Fest einplanen
              </Button>
              <IconButton icon={Pencil} label={`${w.name} bearbeiten`} variant="ghost" size="sm" onClick={() => onBearbeiten(w.planned_item_id)} />
              <IconButton icon={Trash2} label={`${w.name} löschen`} variant="ghost" size="sm" onClick={() => onLoeschen(w.planned_item_id)} />
            </>
          ),
        };
      })}
    />
  );
}

/**
 * „−2 Monate“, „+3 Monate“, „ohne Wirkung“, oder der Sonderfall in Worten.
 *
 * `null` heißt nicht null Monate, sondern „nicht als Zahl ausdrückbar“: Der
 * Posten reißt die Runway erst auf oder trägt sie über den Horizont.
 */
function monatsText(delta: number | null): string {
  if (delta === null) return 'ändert die Runway';
  if (delta === 0) return 'ohne Wirkung';
  const monate = Math.abs(delta) === 1 ? 'Monat' : 'Monate';
  return `${delta > 0 ? '+' : '−'}${Math.abs(delta)} ${monate}`;
}

/** Wann das Geld mit diesem Posten ausgeht. */
function reichtBisText(w: DraftImpact): string {
  return w.runway_date ? formatMonat(w.runway_date) : 'über den Horizont';
}
